import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import ArticleCard from '../components/ArticleCard';
import articlesData from '../data/articles.json';

interface Article {
  id: number;
  title: string;
  excerpt: string;
  content: {
    sections: {
      heading: string;
      body: string;
    }[];
  };
  image: string;
  category: string;
  tags: string[];
  authorName: string;
  authorImage: string;
  authorBio: string;
  publishDate: string;
  readingTime: string;
  sources: string[];
}

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--off-white);
`;

const HeroSection = styled.section`
  background: var(--carbon-gradient);
  padding: var(--space-20) 0 var(--space-16);
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="%23333" stroke-width="0.5" opacity="0.3"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    opacity: 0.1;
  }
  
  @media (max-width: 768px) {
    padding: var(--space-16) 0 var(--space-12);
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--space-6);
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  color: var(--white);
  font-size: var(--text-6xl);
  font-weight: var(--font-black);
  margin-bottom: var(--space-6);
  background: linear-gradient(135deg, var(--white) 0%, var(--racing-red) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: var(--text-4xl);
  }
`;

const HeroSubtitle = styled.p`
  color: var(--chrome-silver);
  font-size: var(--text-xl);
  font-weight: var(--font-medium);
  margin-bottom: var(--space-8);
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: var(--text-lg);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--space-6);
  max-width: 800px;
  margin: 0 auto;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: var(--text-3xl);
  font-weight: var(--font-black);
  color: var(--racing-red);
  margin-bottom: var(--space-2);
`;

const StatLabel = styled.div`
  color: var(--chrome-silver);
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MainContent = styled.main`
  max-width: 1400px;
  margin: 0 auto;
  padding: var(--space-16) var(--space-6);
  
  @media (max-width: 768px) {
    padding: var(--space-12) var(--space-4);
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--space-12);
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: var(--space-8);
  }
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
`;

const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  
  @media (max-width: 1024px) {
    order: -1;
  }
`;

const SectionTitle = styled.h2`
  font-family: 'Rajdhani', sans-serif;
  font-size: var(--text-3xl);
  font-weight: var(--font-black);
  color: var(--carbon-black);
  margin-bottom: var(--space-6);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 4px;
    background: var(--racing-gradient);
    border-radius: var(--radius-full);
  }
`;

const SidebarTitle = styled.h3`
  font-family: 'Rajdhani', sans-serif;
  font-size: var(--text-2xl);
  font-weight: var(--font-bold);
  color: var(--carbon-black);
  margin-bottom: var(--space-4);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 40px;
    height: 3px;
    background: var(--electric-gradient);
    border-radius: var(--radius-full);
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: var(--space-4);
  margin-bottom: var(--space-8);
  flex-wrap: wrap;
  
  @media (max-width: 640px) {
    gap: var(--space-2);
  }
`;

const FilterButton = styled.button<{ isActive: boolean }>`
  background: ${props => props.isActive ? 'var(--racing-gradient)' : 'var(--white)'};
  color: ${props => props.isActive ? 'var(--white)' : 'var(--carbon-black)'};
  border: 2px solid ${props => props.isActive ? 'transparent' : 'var(--medium-gray)'};
  padding: var(--space-3) var(--space-6);
  border-radius: var(--radius-full);
  font-family: 'Rajdhani', sans-serif;
  font-size: var(--text-base);
  font-weight: var(--font-semibold);
  cursor: pointer;
  transition: all var(--transition-fast);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &:hover {
    background: ${props => props.isActive ? 'var(--racing-red-dark)' : 'var(--light-gray)'};
    border-color: ${props => props.isActive ? 'transparent' : 'var(--racing-red)'};
    transform: translateY(-2px);
  }
  
  @media (max-width: 640px) {
    padding: var(--space-2) var(--space-4);
    font-size: var(--text-sm);
  }
`;

const FeaturedSection = styled.section`
  margin-bottom: var(--space-12);
`;

const ArticlesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: var(--space-8);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--space-6);
  }
`;

const CompactGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
`;

const SidebarGrid = styled.div`
  display: grid;
  gap: var(--space-4);
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }
`;

const LoadMoreButton = styled.button`
  background: var(--carbon-gradient);
  color: var(--white);
  border: none;
  padding: var(--space-4) var(--space-8);
  border-radius: var(--radius-full);
  font-family: 'Rajdhani', sans-serif;
  font-size: var(--text-lg);
  font-weight: var(--font-bold);
  cursor: pointer;
  transition: all var(--transition-fast);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: var(--space-8) auto 0;
  display: block;
  box-shadow: var(--shadow-md);
  
  &:hover {
    background: var(--racing-gradient);
    transform: translateY(-2px);
    box-shadow: var(--shadow-racing);
  }
`;

const HomePage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [visibleArticles, setVisibleArticles] = useState<number>(6);

  // Get articles data
  const articles = useMemo(() => {
    return articlesData as Article[];
  }, []);

  const categories = ['all', 'electric', 'sports', 'luxury', 'hybrid', 'suv'];

  const filteredArticles = useMemo(() => {
    if (selectedCategory === 'all') return articles;
    return articles.filter(article => 
      article.category.toLowerCase() === selectedCategory
    );
  }, [articles, selectedCategory]);

  const featuredArticle = filteredArticles[0];
  const mainArticles = filteredArticles.slice(1, visibleArticles + 1);
  
  // Sidebar articles are static and don't change with category filtering
  const sidebarArticles = articles.slice(-8, -3); // Last 5 articles for trending
  const compactArticles = articles.slice(-13, -8); // Previous 5 for quick reads

  const handleLoadMore = () => {
    setVisibleArticles(prev => prev + 6);
  };

  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>CarBuzz.fit</HeroTitle>
          <HeroSubtitle>
            Your ultimate destination for automotive excellence. From cutting-edge reviews 
            to industry insights, we fuel your passion for cars.
          </HeroSubtitle>
          <StatsGrid>
            <StatItem>
              <StatNumber>500+</StatNumber>
              <StatLabel>Car Reviews</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>1M+</StatNumber>
              <StatLabel>Monthly Readers</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>50+</StatNumber>
              <StatLabel>Expert Writers</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>24/7</StatNumber>
              <StatLabel>Latest Updates</StatLabel>
            </StatItem>
          </StatsGrid>
        </HeroContent>
      </HeroSection>

      <MainContent>
        <FilterBar>
          {categories.map(category => (
            <FilterButton
              key={category}
              isActive={selectedCategory === category}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </FilterButton>
          ))}
        </FilterBar>

        <ContentGrid>
          <MainColumn>
            {featuredArticle && (
              <FeaturedSection>
                <SectionTitle>Featured Story</SectionTitle>
                <ArticleCard article={featuredArticle} variant="featured" />
              </FeaturedSection>
            )}

            <section>
              <SectionTitle>Latest Articles</SectionTitle>
              <ArticlesGrid>
                {mainArticles.map(article => (
                  <ArticleCard key={article.id} article={article} variant="standard" />
                ))}
              </ArticlesGrid>
              
              {visibleArticles < filteredArticles.length && (
                <LoadMoreButton onClick={handleLoadMore}>
                  Load More Articles
                </LoadMoreButton>
              )}
            </section>
          </MainColumn>

          <Sidebar>
            <section>
              <SidebarTitle>Trending Now</SidebarTitle>
              <SidebarGrid>
                {sidebarArticles.map(article => (
                  <ArticleCard key={article.id} article={article} variant="sidebar" />
                ))}
              </SidebarGrid>
            </section>

            <section>
              <SidebarTitle>Quick Reads</SidebarTitle>
              <CompactGrid>
                {compactArticles.map(article => (
                  <ArticleCard key={article.id} article={article} variant="compact" />
                ))}
              </CompactGrid>
            </section>
          </Sidebar>
        </ContentGrid>
      </MainContent>
    </PageContainer>
  );
};

export default HomePage;
